<template>
  <div
    :style="{'--tree-node-indent': `${indent}px`}"
    class="tree-select-dropdown-node-container"
  >
    <div
      tabindex="0"
      class="tree-select-dropdown-node no-focus-visible focus-element"
      :class="{'is-visible': isVisible}"
    >
      <div
        class="tree-select-dropdown-node__open-trigger"
        data-dropdown
        :class="{
          'tree-select-dropdown-node__open-trigger_active': nodeIsOpen,
          'tree-select-dropdown-node__open-trigger_no-child': isLeafNode
        }"
        @click="triggerDropdown"
      >
        <MIcon
          :icon="iconArrow"
        />
      </div>
      <div
        v-if="isNodeSelectable"
        class="tree-select-dropdown-node__checkbox"
      >
        <MCheckbox
          is-not-focus
          :disabled="readonly"
          :label-text="nodeName"
          :value="isNodeActive"
          data-checkbox
          @input="handleCheckbox"
          @handle-click-navigation="$emit('handle-click-navigation', $event)"
        />
      </div>
      <div
        v-else
        v-overflow-title="nodeName"
        class="tree-select-dropdown-node__text"
      >
        {{ nodeName }}
      </div>
    </div>
    <div
      v-show="nodeIsOpen"
    >
      <template v-if="nodeIsLoaded">
        <div
          v-if="childNodes.length === 0"
          class="tree-select-dropdown-node__no-child"
          :class="{'tree-select-dropdown-node__no-child_no-select': !isNodeSelectable}"
        >
          {{ noChildText }}
        </div>
        <TreeSelectDropdownNode
          v-for="child in childNodes"
          v-else
          :key="child.id"
          :readonly="readonly"
          :flat-choices="flatChoices"
          :force-open="forceOpen"
          :node-id="child.id"
          :indent="indent || 22"
          :is-visible="nodeIsOpen"
          :selected-items="selectedItems"
          :no-child-text-getter="noChildTextGetter"
          :is-leaf-node-predicate="isLeafNodePredicate"
          @openNode="handleChildMountedOpen"
          @triggerCheckbox="$emit('triggerCheckbox', $event)"
          @handle-click-navigation="$emit('handle-click-navigation', $event)"
        />
      </template>
      <template v-else>
        <div class="tree-select-dropdown-node_loading">
          <SkeletonLoader
            :is-loading="true"
            width="100%"
            height="8px"
          />
        </div>
      </template>
    </div>
  </div>
</template>

<script>
  import MIcon from 'components/Design/MIcon';
  import MCheckbox from 'components/Inputs/Basic/MCheckbox';
  import iconArrow from 'assets/icons/bullet-arrow.svg';
  import SkeletonLoader from 'components/common/SkeletonLoader';
  import _ from 'lodash';

  _.noConflict();

  export default {
    name: 'TreeSelectDropdownNode',
    components: {
      MIcon,
      MCheckbox,
      SkeletonLoader
    },
    props: {
      nodeId: {
        type: [String, Number],
        required: true
      },
      flatChoices: {
        type: Object,
        required: true
      },
      indent: {
        type: Number,
        default: 0
      },
      selectedItems: {
        type: Array,
        default: Array
      },
      forceOpen: {
        type: Boolean,
        default: false
      },
      isVisible: {
        type: Boolean,
        default: true
      },
      readonly: {
        type: Boolean,
        default: false
      },
      noChildTextGetter: {
        type: Function,
        required: true
      },
      isLeafNodePredicate: {
        type: Function,
        default: undefined
      }
    },
    data () {
      return {
        iconArrow,
        nodeIsOpen: false
      };
    },
    computed: {
      isLeafNode () {
        if (this.isLeafNodePredicate) {
          return this.isLeafNodePredicate(this.currentNode);
        }
        return !this.hasChildNodes;
      },
      noChildText () {
        return this.noChildTextGetter(this.currentNode);
      },
      isNodeSelectable () {
        if (this.currentNode.selectable === undefined) {
          return true;
        }
        return this.currentNode.selectable;
      },
      isNodeActive () {
        return Boolean(this.selectedItems.find(i => i === this.nodeId));
      },
      currentNode () {
        return this.flatChoices[this.nodeId];
      },
      nodeName () {
        return this.currentNode.name;
      },
      nodeIsLoaded () {
        return this.currentNode.isLoaded === undefined || this.currentNode.isLoaded;
      },
      childNodes () {
        return _.orderBy(Object.keys(this.flatChoices).
          map(i => this.flatChoices[i]).
          filter(i => i?.parentId === this.nodeId), ['name']);
      },
      openCallback () {
        return this.currentNode.openCallback;
      },
      hasChildNodes () {
        if (this.nodeIsLoaded) {
          return this.childNodes.length > 0;
        }
        return true;
      }
    },
    watch: {
      isNodeActive: {
        immediate: true,
        handler () {
          if (this.isNodeActive) {
            this.handleChildMountedOpen();
          }
        }
      },
      forceOpen: {
        immediate: true,
        handler () {
          if (this.forceOpen && !this.nodeIsOpen) {
            this.triggerDropdown();
          }
        }
      }
    },
    methods: {
      handleCheckbox () {
        this.$emit('triggerCheckbox', this.nodeId);
      },
      handleChildMountedOpen () {
        if (this.nodeIsOpen) {
          return;
        }
        this.triggerDropdown();
        this.$emit('openNode');
      },
      triggerDropdown () {
        if (this.isLeafNode) {
          return;
        }
        if (!this.nodeIsLoaded && this.openCallback) {
          this.openCallback();
        }
        this.nodeIsOpen = !this.nodeIsOpen;
      }
    }
  };
</script>

<style>
  .tree-select-dropdown-node-container {
    padding-left: var(--tree-node-indent);
    padding-right: 2px;
  }

  .tree-select-dropdown-node {
    font-size: 14px;

    overflow: hidden;

    display: flex;

    align-items: center;

    width: 100%;
    height: 24px;

    &:focus {
      background-color: var(--action-background-color);
    }
  }

  .tree-select-dropdown-node__no-child {
    font-size: 12px;

    color: var(--gray-text-color);

    display: flex;

    align-items: center;

    height: 18px;

    margin-left: 46px;
  }

  .tree-select-dropdown-node__no-child_no-select {
    margin-left: 24px;
  }

  .tree-select-dropdown-node__open-trigger {
    cursor: pointer;

    display: flex;

    justify-content: center;
    align-items: center;

    width: 16px;
    height: 16px;

    margin-left: 4px;
    margin-right: 4px;

    border-radius: 2px;
  }

  .tree-select-dropdown-node__open-trigger:hover {
    background: var(--action-background-color);
  }

  .tree-select-dropdown-node__open-trigger_no-child {
    cursor: default;
    opacity: 0;
  }

  .tree-select-dropdown-node__open-trigger_no-child:hover {
    background: none;
  }

  .tree-select-dropdown-node__open-trigger svg {
    width: 16px;
    height: 16px;

    transition: 150ms;
  }

  .tree-select-dropdown-node__open-trigger_active svg {
    transform: rotate(90deg);
  }

  .tree-select-dropdown-node__checkbox,
  .tree-select-dropdown-node__text {
    overflow: hidden;
    width: 100%;
  }

  .tree-select-dropdown-node__text {
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .tree-select-dropdown-node_loading {
    margin: 8px 24px;
  }
</style>
